import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { supabase } from '../../supabaseClient';
import { COMM_MAX_STUDENTS, commStudentLink } from '../../services/realtime/commClient';
import type { CommHostRoom } from '../../hooks/useCommHost';

const randomCode = () => {
  const b = new Uint32Array(1);
  crypto.getRandomValues(b);
  return String(100000 + (b[0] % 900000));
};
const hhmm = (t: number) => new Date(t).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });

/** 공유 QR(PNG): QR + 반 이름 + 입장번호 */
async function downloadCommQr(link: string, className: string, entryCode: string) {
  const qr = document.createElement('canvas');
  await QRCode.toCanvas(qr, link, { errorCorrectionLevel: 'M', margin: 2, width: 720 });
  const pad = 48;
  const W = qr.width + pad * 2;
  const H = qr.height + pad + 200;
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d');
  if (!g) return;
  g.fillStyle = '#ffffff';
  g.fillRect(0, 0, W, H);
  g.drawImage(qr, pad, pad);
  g.fillStyle = '#12302c';
  g.textAlign = 'center';
  const font = '"Pretendard", "Apple SD Gothic Neo", "Noto Sans KR", sans-serif';
  g.font = `800 52px ${font}`;
  g.fillText(className.slice(0, 24), W / 2, qr.height + pad + 76, W - pad * 2);
  g.font = `700 44px ${font}`;
  g.fillStyle = '#0f766e';
  g.fillText(`입장번호 ${entryCode}`, W / 2, qr.height + pad + 148);
  const url = c.toDataURL('image/png');
  const a = document.createElement('a');
  a.href = url;
  a.download = `개별디바이스-${className.replace(/[\\/:*?"<>|\s]+/g, '_').slice(0, 30) || '반'}-QR.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const CREATE_ERR: Record<string, string> = {
  not_paid: '개별 디바이스 프레젠테이션은 유료 이용권 전용이에요.',
  unauthorized: '로그인이 필요해요.',
  bad_input: '반 이름·이름·입장번호(숫자 4~8자리)를 확인해 주세요.',
  network: '네트워크 오류 — 잠시 후 다시 시도해 주세요.',
};

/** 리모컨 시트 안: 소통 프레젠테이션 만들기 / 공유 정보·종료 */
const RemoteCommRoomCard: React.FC<{
  room: CommHostRoom | null;
  defaultClassName: string;
  onCreate: (input: { className: string; teacherName: string; entryCode: string }) => Promise<string | null>;
  onEnd: () => Promise<boolean>;
  roster?: React.ReactNode;
}> = ({ room, defaultClassName, onCreate, onEnd, roster }) => {
  const [className, setClassName] = useState(defaultClassName.slice(0, 40));
  const [teacherName, setTeacherName] = useState('');
  const [entryCode, setEntryCode] = useState(randomCode);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const shareLink = room ? commStudentLink(room.roomId) : '';
  useEffect(() => {
    if (!shareLink) return undefined;
    let alive = true;
    void QRCode.toDataURL(shareLink, { errorCorrectionLevel: 'M', margin: 1, width: 400 })
      .then((u) => alive && setQrUrl(u))
      .catch(() => alive && setQrUrl(''));
    return () => {
      alive = false;
    };
  }, [shareLink]);

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      const meta = (data.session?.user?.user_metadata ?? {}) as Record<string, unknown>;
      const name =
        (typeof meta.full_name === 'string' && meta.full_name.trim()) ||
        (typeof meta.name === 'string' && meta.name.trim()) ||
        '';
      if (name) setTeacherName((v) => v || name.slice(0, 30));
    });
  }, []);

  if (room) {
    const link = commStudentLink(room.roomId);
    const copy = async () => {
      try {
        await navigator.clipboard.writeText(link);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      } catch {
        window.prompt('링크를 복사하세요', link);
      }
    };
    return (
      <div className="cp-rc-comm-card">
        <p className="cp-rc-comm-room">
          <b>{room.className}</b>
          <span>{room.teacherName}</span>
          <small>{hhmm(room.expiresAt)}까지 (자동 종료)</small>
        </p>
        <div className="cp-rc-comm-link">
          <input className="cp-remote-input" readOnly value={link} onFocus={(e) => e.currentTarget.select()} aria-label="휴대용 디바이스 링크" />
          <button type="button" className="cp-remote-btn cp-remote-btn--primary cp-rc-comm-action" onClick={copy}>
            {copied ? '복사됨' : '복사'}
          </button>
        </div>
        {qrUrl ? (
          <div className="cp-rc-comm-qr">
            <img src={qrUrl} alt="휴대용 디바이스 입장 QR 코드" width={200} height={200} />
            <button
              type="button"
              className="cp-remote-btn cp-remote-btn--primary cp-rc-comm-action"
              onClick={() => void downloadCommQr(link, room.className, room.entryCode).catch(() => setErr('QR을 저장하지 못했어요.'))}
            >
              QR 다운로드
            </button>
          </div>
        ) : null}
        <div className="cp-rc-comm-code">
          <span>입장번호</span>
          <b>{room.entryCode}</b>
        </div>
        {roster}
        {err ? <p className="cp-remote-rename-error">{err}</p> : null}
        <button
          type="button"
          className="cp-remote-btn cp-remote-btn--primary cp-rc-comm-end"
          disabled={busy}
          onClick={async () => {
            if (!window.confirm('개별 디바이스 프레젠테이션을 종료할까요? 연결된 기기와 응답이 모두 삭제돼요.')) return;
            setBusy(true);
            setErr('');
            const ok = await onEnd();
            setBusy(false);
            if (!ok) setErr('종료하지 못했어요. 연결 상태를 확인하고 다시 시도해 주세요.');
          }}
        >
          {busy ? '종료 중…' : '개별 디바이스 프레젠테이션 종료'}
        </button>
      </div>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    const input = { className: className.trim(), teacherName: teacherName.trim(), entryCode: entryCode.trim() };
    if (!input.className || !input.teacherName || !/^\d{4,8}$/.test(input.entryCode)) {
      setErr(CREATE_ERR.bad_input);
      return;
    }
    setBusy(true);
    const r = await onCreate(input);
    setBusy(false);
    if (r) setErr(CREATE_ERR[r] ?? `만들지 못했어요 (${r})`);
  };
  return (
    <form className="cp-rc-comm-card" onSubmit={submit}>
      <p className="cp-rc-comm-hint">
        휴대용 디바이스에서 링크로 들어와 공지를 보고 질문에 답해요. 최대 {COMM_MAX_STUDENTS}명 · 6시간 후 자동 종료.
      </p>
      <label className="cp-rc-comm-field">
        <span>반 이름</span>
        <input className="cp-remote-input" value={className} maxLength={40} onChange={(e) => setClassName(e.target.value)} placeholder="예: 5학년 2반 과학" />
      </label>
      <label className="cp-rc-comm-field">
        <span>내 이름</span>
        <input className="cp-remote-input" value={teacherName} maxLength={30} onChange={(e) => setTeacherName(e.target.value)} placeholder="휴대용 디바이스 화면에 보일 이름" />
      </label>
      <label className="cp-rc-comm-field">
        <span>입장번호 (숫자 4~8자리)</span>
        <div className="cp-rc-comm-row">
          <input
            className="cp-remote-input"
            value={entryCode}
            inputMode="numeric"
            maxLength={8}
            onChange={(e) => setEntryCode(e.target.value.replace(/\D/g, ''))}
          />
          <button type="button" className="cp-remote-btn" onClick={() => setEntryCode(randomCode())}>
            새 번호
          </button>
        </div>
      </label>
      {err ? <p className="cp-remote-rename-error">{err}</p> : null}
      <button type="submit" className="cp-remote-btn cp-remote-btn--primary cp-rc-comm-create" disabled={busy}>
        {busy ? '만드는 중…' : '만들기'}
      </button>
    </form>
  );
};

export default RemoteCommRoomCard;
